import { clsx } from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import Toggle from '../../../../common/Toggle';
import BlockTitle from '../../../layout/BlockTitle';
import GrayBox from '../../../layout/GrayBox';
import Tooltip from '../../../../common/Tooltip';
import { getBoldText, getItalicText } from '../../../../common/FormatMessageComponents';
import shieldIcon from '../../../../../../assets/icons/devices-router/shield-icon.svg';
import lightningdIcon from '../../../../../../assets/icons/devices-router/lightning-icon.svg';
import generalStyles from '../RouterSettingsSection.scss';
import styles from './RouterConfigEnhancersBlock.scss';

const tooltipClassName = 'toggleTooltip';

interface RouterConfigEnhancersBlockProps {
  isConfigSelected: boolean;
  onEnhancerChange: (enhancer: 'general' | 'security' | 'adblock' | 'adsec') => void;
  selectedEnhancer?: 'general' | 'security' | 'adblock' | 'adsec';
  disabled?: boolean;
  isExistingRouter?: boolean;
}

const RouterConfigEnhancersBlock = ({
  isConfigSelected,
  selectedEnhancer = 'general',
  onEnhancerChange,
  disabled,
  isExistingRouter,
}: RouterConfigEnhancersBlockProps) => {
  const intl = useIntl();

  const isThretShild = ['security', 'adsec'].includes(selectedEnhancer);
  const isAdblock = ['adblock', 'adsec'].includes(selectedEnhancer);

  const onThreatShieldChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const isChecked = event.target.checked;

    switch (selectedEnhancer) {
      case 'general':
      case 'security': {
        onEnhancerChange(isChecked ? 'security' : 'general');
        break;
      }

      case 'adblock':
      case 'adsec': {
        onEnhancerChange(isChecked ? 'adsec' : 'adblock');
        break;
      }

      default:
    }
  };

  const onAdblockChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const isChecked = event.target.checked;

    switch (selectedEnhancer) {
      case 'general':
      case 'adblock': {
        onEnhancerChange(isChecked ? 'adblock' : 'general');
        break;
      }

      case 'security':
      case 'adsec': {
        onEnhancerChange(isChecked ? 'adsec' : 'security');
        break;
      }

      default:
    }
  };

  let tooltipText = '';

  if (disabled) {
    if (isExistingRouter) {
      tooltipText = intl.formatMessage({
        id: 'profile.routerSection.enhancersBlock.cannotChangeExistingRouterSettings',
        defaultMessage: 'You can’t change the settings for this device',
      });
    } else if (isConfigSelected) {
      tooltipText = intl.formatMessage({
        id: 'profile.routerSection.enhancersBlock.notForStreamingTooltip',
        defaultMessage: "Feature doesn't work with streaming mode",
      });
    } else {
      tooltipText = intl.formatMessage({
        id: 'profile.routerSection.enhancersBlock.selectCountryFirst',
        defaultMessage: 'Please select the country you want to connect to',
      });
    }
  }

  return (
    <article>
      <BlockTitle className={styles.sectionTitle}>
        <FormattedMessage id="profile.routerSection.enhancersBlock.title" defaultMessage="Protect your router" />
      </BlockTitle>
      <p className={generalStyles.text}>
        <FormattedMessage
          id="profile.routerSection.enhancersBlock.subtitle"
          defaultMessage="Secure your router and improve online safety with our handy set of features.{br} <i><b>Note:</b> these features require active VPN connection and don't work with streaming mode.</i>"
          values={{
            b: getBoldText(),
            br: <br />,
            i: getItalicText(),
          }}
        />
      </p>

      <GrayBox className={styles.enhancersBlock}>
        <div className={styles.toggleTitle}>
          <BlockTitle className={styles.toggleTitleLabel}>
            <img src={shieldIcon} width="20" height="20" alt="" />
            <FormattedMessage
              id="profile.routerSection.enhancersBlock.threatShield.title"
              defaultMessage="Online Threat Shield"
            />
          </BlockTitle>
          <Toggle
            isChecked={isThretShild}
            disabled={disabled || isExistingRouter}
            handleChange={onThreatShieldChange}
            className={tooltipClassName}
          />
        </div>

        <ul className={styles.enhancersList}>
          <li>
            <FormattedMessage
              id="profile.routerSection.enhancersBlock.threatShield.list.1"
              defaultMessage="Blocks access to harmful domains"
            />
          </li>
          <li>
            <FormattedMessage
              id="profile.routerSection.enhancersBlock.threatShield.list.2"
              defaultMessage="Defends you from scam and phishing"
            />
          </li>
          <li>
            <FormattedMessage
              id="profile.routerSection.enhancersBlock.threatShield.list.3"
              defaultMessage="Protects from typosquating, cryptojacking and other attacks"
            />
          </li>
        </ul>

        <hr className={clsx(generalStyles.divider, styles.enhancersDivider)} />

        <div className={styles.toggleTitle}>
          <BlockTitle className={styles.toggleTitleLabel}>
            <img src={lightningdIcon} width="20" height="20" alt="" />
            <FormattedMessage id="profile.routerSection.enhancersBlock.adblock.title" defaultMessage="DNS Adblock" />
          </BlockTitle>
          <Toggle
            isChecked={isAdblock}
            disabled={disabled || isExistingRouter}
            handleChange={onAdblockChange}
            className={tooltipClassName}
          />
        </div>

        <ul className={styles.enhancersList}>
          <li>
            <FormattedMessage
              id="profile.routerSection.enhancersBlock.adblock.list.1"
              defaultMessage="Protects from malicious ads"
            />
          </li>
          <li>
            <FormattedMessage
              id="profile.routerSection.enhancersBlock.adblock.list.2"
              defaultMessage="Blocks intrusive pop-ups"
            />
          </li>
          <li>
            <FormattedMessage
              id="profile.routerSection.enhancersBlock.adblock.list.3"
              defaultMessage="Disables disguised third-party trackers"
            />
          </li>
        </ul>
      </GrayBox>

      <Tooltip anchorSelect={`.${tooltipClassName}`}>{tooltipText}</Tooltip>
    </article>
  );
};

export default RouterConfigEnhancersBlock;
